import React, { useState } from 'react';
import { Link, Copy, Check, ArrowDownUp, AlertCircle } from 'lucide-react';

const UrlEncoder: React.FC = () => {
  const [decoded, setDecoded] = useState('');
  const [encoded, setEncoded] = useState('');
  const [mode, setMode] = useState<'encode' | 'decode'>('encode');
  const [encodeType, setEncodeType] = useState<'component' | 'full'>('component');
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState('');

  const encode = (input: string) => {
    try {
      setError('');
      const result = encodeType === 'component' 
        ? encodeURIComponent(input)
        : encodeURI(input);
      setEncoded(result);
    } catch (e) {
      setError('Failed to encode URL');
    }
  };

  const decode = (input: string) => {
    try {
      setError('');
      const result = encodeType === 'component'
        ? decodeURIComponent(input)
        : decodeURI(input);
      setDecoded(result);
    } catch (e) {
      setError('Invalid encoded URL');
    }
  };

  const handleDecodedChange = (value: string) => {
    setDecoded(value);
    if (mode === 'encode') encode(value);
  };

  const handleEncodedChange = (value: string) => {
    setEncoded(value);
    if (mode === 'decode') decode(value);
  };

  const toggleMode = () => {
    setMode(mode === 'encode' ? 'decode' : 'encode');
    setError('');
  };

  const copyToClipboard = async (content: string, type: string) => {
    await navigator.clipboard.writeText(content);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const clearAll = () => {
    setDecoded('');
    setEncoded('');
    setError('');
  };

  const examples = [
    { label: 'Space', decoded: 'hello world', encoded: 'hello%20world' },
    { label: 'Special chars', decoded: 'name=John&age=30', encoded: 'name%3DJohn%26age%3D30' },
    { label: 'Unicode', decoded: 'café ☕', encoded: 'caf%C3%A9%20%E2%98%95' },
  ];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">URL Encoder/Decoder</h2>
        <p className="text-gray-600">Encode or decode URLs and query parameters</p>
      </div>

      <div className="max-w-3xl mx-auto space-y-4">
        {/* Mode & Type Toggle */}
        <div className="flex justify-center gap-4">
          <div className="inline-flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setMode('encode')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === 'encode' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Encode
            </button>
            <button
              onClick={() => setMode('decode')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === 'decode' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Decode
            </button>
          </div>
          <select
            value={encodeType}
            onChange={(e) => setEncodeType(e.target.value as 'component' | 'full')}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="component">encodeURIComponent</option>
            <option value="full">encodeURI</option>
          </select>
        </div>

        {/* Input/Output */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Decoded Input */}
          <div className={`bg-white rounded-xl border-2 p-4 ${mode === 'encode' ? 'border-blue-500' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700">
                {mode === 'encode' ? 'Decoded (Input)' : 'Decoded (Output)'}
              </label>
              <button
                onClick={() => copyToClipboard(decoded, 'decoded')}
                className="p-1 text-gray-500 hover:text-gray-700"
                disabled={!decoded}
              >
                {copied === 'decoded' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <textarea
              value={decoded}
              onChange={(e) => handleDecodedChange(e.target.value)}
              readOnly={mode === 'decode'}
              className={`w-full h-32 px-3 py-2 font-mono text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 ${
                mode === 'decode' ? 'bg-gray-50' : ''
              }`}
              placeholder={mode === 'encode' ? 'Enter text to encode...' : 'Decoded text will appear here...'}
            />
          </div>

          {/* Encoded Input */}
          <div className={`bg-white rounded-xl border-2 p-4 ${mode === 'decode' ? 'border-blue-500' : 'border-gray-200'}`}>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-700">
                {mode === 'decode' ? 'Encoded (Input)' : 'Encoded (Output)'}
              </label>
              <button
                onClick={() => copyToClipboard(encoded, 'encoded')}
                className="p-1 text-gray-500 hover:text-gray-700"
                disabled={!encoded}
              >
                {copied === 'encoded' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <textarea
              value={encoded}
              onChange={(e) => handleEncodedChange(e.target.value)}
              readOnly={mode === 'encode'}
              className={`w-full h-32 px-3 py-2 font-mono text-sm border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 ${
                mode === 'encode' ? 'bg-gray-50' : ''
              }`}
              placeholder={mode === 'decode' ? 'Enter URL to decode...' : 'Encoded URL will appear here...'}
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-center gap-3">
          <button
            onClick={toggleMode}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ArrowDownUp className="w-4 h-4" />
            Switch to {mode === 'encode' ? 'Decode' : 'Encode'}
          </button>
          <button
            onClick={clearAll}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Clear
          </button>
        </div>

        {/* Examples */}
        <div className="bg-gray-50 rounded-xl p-4">
          <label className="block text-sm font-medium text-gray-700 mb-3">Examples</label>
          <div className="grid grid-cols-3 gap-2">
            {examples.map((ex) => (
              <button
                key={ex.label}
                onClick={() => {
                  setDecoded(ex.decoded);
                  setEncoded(ex.encoded);
                }}
                className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm hover:bg-gray-100 transition-colors text-left"
              >
                <span className="font-medium">{ex.label}</span>
                <p className="text-xs text-gray-500 truncate">{ex.decoded}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <div className="flex items-start gap-3">
            <Link className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-2">URL Encoding</h4>
              <p className="text-sm text-blue-700">
                <strong>encodeURIComponent:</strong> Encodes all special characters including /, ?, &, =. Use for query parameters.<br/>
                <strong>encodeURI:</strong> Preserves URL structure characters. Use for full URLs.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UrlEncoder;
